import {
  Alert,
  Badge,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArchive, IconPlus, IconRotate } from '@tabler/icons-react';
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EXEMPLAR_TREATMENT_PLAN_TAG, TEMPLATE_CATEGORY_SYSTEM } from '@unify/fhir-model/constants';
import { useRoleFlags } from '@/hooks/useRbac';
import {
  buildExemplarTreatmentPlanSearchParams,
  createDeidentifiedExemplarCopy,
  getExemplarTreatmentPlanTitle,
  getTreatmentPlanAiPrompt,
  getTreatmentPlanExemplarReferences,
  setExemplarTreatmentPlanTitle,
  setTreatmentPlanAiPrompt,
  setTreatmentPlanExemplarReferences,
  validateExemplarHasNoPatientIdentity,
} from '@/lib/treatmentPlanAiConfig';
import {
  DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT,
  DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT,
} from '@/lib/treatmentPlanDefaultPrompts';
import { usesSpecializedTreatmentPlanForm } from '@/lib/chart/chartDocumentTemplates';
import { TreatmentPlanDocumentFormSection } from '@/pages/clients/components/TreatmentPlanDocumentFormSection';
import type { VariableContext } from '@/lib/template-variables';

const EMPTY_VAR_CTX: VariableContext = {};

type PromptKind = 'guided' | 'generation';

const GUIDED_PROMPT_HELP =
  'Practice-level instructions for the longer guided planner interview (~3–5 checklist turns). Blank '
  + 'uses the built-in guided conversation prompt shown below. A separate, non-editable clinical safety '
  + 'layer always applies first and cannot be changed or disabled here.';

const GENERATION_PROMPT_HELP =
  'Practice-level instructions for Quick generate (documentation-first full generation, ~1–3 turns) and '
  + 'for drafting the plan once ready. Relies on signed intake notes and Zoom intake transcripts when '
  + 'available. Blank uses the built-in full-generation prompt shown below. A separate, non-editable '
  + 'clinical safety layer always applies first and cannot be changed or disabled here.';

export function AiTreatmentPlanningSettings(): JSX.Element {
  const medplum = useMedplum();
  const { isAdmin } = useRoleFlags();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<Questionnaire[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [guidedPrompt, setGuidedPrompt] = useState(DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT);
  const [generationPrompt, setGenerationPrompt] = useState(DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT);
  const [savedGuidedPrompt, setSavedGuidedPrompt] = useState(DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT);
  const [savedGenerationPrompt, setSavedGenerationPrompt] = useState(DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT);
  /** Whether the template currently has a saved practice override (vs. showing the built-in default). */
  const [guidedHasOverride, setGuidedHasOverride] = useState(false);
  const [generationHasOverride, setGenerationHasOverride] = useState(false);
  const [resetConfirmKind, setResetConfirmKind] = useState<PromptKind | null>(null);
  const [selectedExemplarRefs, setSelectedExemplarRefs] = useState<string[]>([]);
  const [savedExemplarRefs, setSavedExemplarRefs] = useState<string[]>([]);
  const [availableExemplars, setAvailableExemplars] = useState<QuestionnaireResponse[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingExemplar, setEditingExemplar] = useState<QuestionnaireResponse | null>(null);
  const [exemplarTitle, setExemplarTitle] = useState('');
  const [liveExemplar, setLiveExemplar] = useState<QuestionnaireResponse | undefined>();
  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const results = await medplum.searchResources('Questionnaire', {
        _tag: `${TEMPLATE_CATEGORY_SYSTEM}|treatment-planning`,
        status: 'active',
        _count: '100',
      });
      const treatmentPlans = results.filter((q) => usesSpecializedTreatmentPlanForm(q));
      setTemplates(treatmentPlans);
      setSelectedTemplateId((current) => current ?? treatmentPlans[0]?.id ?? null);
    } catch (err) {
      notifications.show({
        title: 'Failed to load treatment plan templates',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  const loadTemplateConfig = useCallback(async (template: Questionnaire) => {
    const questionnaireRef = template.url
      ? `${template.url}|${template.version ?? '1.0.0'}`
      : `Questionnaire/${template.id}`;
    const guidedOverride = await getTreatmentPlanAiPrompt(medplum, template, 'guided');
    const generationOverride = await getTreatmentPlanAiPrompt(medplum, template, 'generation');
    const guided = guidedOverride ?? DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT;
    const generation = generationOverride ?? DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT;
    const refs = getTreatmentPlanExemplarReferences(template);
    setGuidedPrompt(guided);
    setGenerationPrompt(generation);
    setSavedGuidedPrompt(guided);
    setSavedGenerationPrompt(generation);
    setGuidedHasOverride(Boolean(guidedOverride));
    setGenerationHasOverride(Boolean(generationOverride));
    setSelectedExemplarRefs(refs);
    setSavedExemplarRefs(refs);

    try {
      const exemplars = await medplum.searchResources(
        'QuestionnaireResponse',
        buildExemplarTreatmentPlanSearchParams(questionnaireRef),
      );
      // Also try bare Questionnaire/id questionnaire values used by some seeds.
      const byId = template.id
        ? await medplum.searchResources(
            'QuestionnaireResponse',
            buildExemplarTreatmentPlanSearchParams(`Questionnaire/${template.id}`),
          )
        : [];
      const merged = new Map<string, QuestionnaireResponse>();
      for (const qr of [...exemplars, ...byId]) {
        if (qr.id) merged.set(qr.id, qr);
      }
      setAvailableExemplars([...merged.values()]);
    } catch {
      setAvailableExemplars([]);
    }
  }, [medplum]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  // Hydrate only when the selected template changes — never on unrelated background re-renders,
  // so in-progress edits are never silently overwritten. Successful saves update state directly
  // (see handleSave) rather than triggering a refetch here.
  useEffect(() => {
    if (!selectedTemplate) return;
    void loadTemplateConfig(selectedTemplate);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on id, not object identity
  }, [loadTemplateConfig, selectedTemplate?.id]);

  const isDirty =
    guidedPrompt !== savedGuidedPrompt
    || generationPrompt !== savedGenerationPrompt
    || selectedExemplarRefs.join('|') !== savedExemplarRefs.join('|');

  if (!isAdmin) {
    return <></>;
  }

  const handleSave = async (): Promise<void> => {
    if (!selectedTemplate?.id) return;
    setSaving(true);
    try {
      const latest = await medplum.readResource('Questionnaire', selectedTemplate.id);
      // Blank means "use the application default" — if the provider's edited text matches the
      // default verbatim, save it as a blank override rather than persisting a redundant copy.
      const trimmedGuided = guidedPrompt.trim();
      const trimmedGeneration = generationPrompt.trim();
      const guidedOverrideToSave =
        trimmedGuided === DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT.trim() ? '' : trimmedGuided;
      const generationOverrideToSave =
        trimmedGeneration === DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT.trim() ? '' : trimmedGeneration;
      await setTreatmentPlanAiPrompt(medplum, latest, 'guided', guidedOverrideToSave);
      await setTreatmentPlanAiPrompt(medplum, latest, 'generation', generationOverrideToSave);
      setTreatmentPlanExemplarReferences(latest, selectedExemplarRefs);
      const saved = await medplum.updateResource(latest);
      setTemplates((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
      const effectiveGuided = guidedOverrideToSave || DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT;
      const effectiveGeneration = generationOverrideToSave || DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT;
      setGuidedPrompt(effectiveGuided);
      setGenerationPrompt(effectiveGeneration);
      setSavedGuidedPrompt(effectiveGuided);
      setSavedGenerationPrompt(effectiveGeneration);
      setGuidedHasOverride(Boolean(guidedOverrideToSave));
      setGenerationHasOverride(Boolean(generationOverrideToSave));
      setSavedExemplarRefs([...selectedExemplarRefs]);
      notifications.show({
        title: 'AI treatment planning settings saved',
        message: 'Prompts and exemplar selections will apply to new AI requests for this template.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdits = (): void => {
    setGuidedPrompt(savedGuidedPrompt);
    setGenerationPrompt(savedGenerationPrompt);
    setSelectedExemplarRefs([...savedExemplarRefs]);
  };

  const promptIsDirty = (kind: PromptKind): boolean =>
    kind === 'guided' ? guidedPrompt !== savedGuidedPrompt : generationPrompt !== savedGenerationPrompt;

  const hasOverride = (kind: PromptKind): boolean =>
    kind === 'guided' ? guidedHasOverride : generationHasOverride;

  const requestReset = (kind: PromptKind): void => {
    if (promptIsDirty(kind) || hasOverride(kind)) {
      setResetConfirmKind(kind);
      return;
    }
    if (kind === 'guided') setGuidedPrompt(DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT);
    else setGenerationPrompt(DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT);
  };

  const confirmReset = (): void => {
    if (resetConfirmKind === 'guided') setGuidedPrompt(DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT);
    else if (resetConfirmKind === 'generation') setGenerationPrompt(DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT);
    setResetConfirmKind(null);
  };

  const openCreateExemplar = () => {
    setConfirmCreateOpen(true);
  };

  const createBlankExemplar = async (): Promise<void> => {
    if (!selectedTemplate?.id) return;
    setConfirmCreateOpen(false);
    const questionnaireRef = selectedTemplate.url
      ? `${selectedTemplate.url}|${selectedTemplate.version ?? '1.0.0'}`
      : `Questionnaire/${selectedTemplate.id}`;
    const blank = createDeidentifiedExemplarCopy(
      { resourceType: 'QuestionnaireResponse', status: 'completed', item: [] },
      'Untitled exemplar treatment plan',
      questionnaireRef,
    );
    try {
      const created = await medplum.createResource(blank);
      if (!validateExemplarHasNoPatientIdentity(created)) {
        throw new Error('Exemplar unexpectedly contains patient identity');
      }
      setAvailableExemplars((prev) => [...prev, created]);
      setEditingExemplar(created);
      setExemplarTitle(getExemplarTreatmentPlanTitle(created) ?? '');
      setLiveExemplar(created);
      setEditorOpen(true);
    } catch (err) {
      notifications.show({
        title: 'Could not create exemplar',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const openEditExemplar = (qr: QuestionnaireResponse) => {
    setEditingExemplar(qr);
    setExemplarTitle(getExemplarTreatmentPlanTitle(qr) ?? '');
    setLiveExemplar(qr);
    setEditorOpen(true);
  };

  const saveExemplar = async (): Promise<void> => {
    if (!editingExemplar || !selectedTemplate) return;
    const title = exemplarTitle.trim();
    if (!title) {
      notifications.show({
        title: 'Title required',
        message: 'Exemplar treatment plans must have an explicit title.',
        color: 'red',
      });
      return;
    }
    const next: QuestionnaireResponse = {
      ...editingExemplar,
      ...(liveExemplar ?? {}),
      status: 'completed',
      subject: undefined,
      encounter: undefined,
      meta: {
        ...editingExemplar.meta,
        tag: [
          ...(editingExemplar.meta?.tag ?? []).filter(
            (tag) =>
              !(
                tag.system === EXEMPLAR_TREATMENT_PLAN_TAG.system
                && tag.code === EXEMPLAR_TREATMENT_PLAN_TAG.code
              ),
          ),
          EXEMPLAR_TREATMENT_PLAN_TAG,
        ],
      },
      item: liveExemplar?.item ?? editingExemplar.item,
    };
    delete next.basedOn;
    delete (next as { partOf?: unknown }).partOf;
    setExemplarTreatmentPlanTitle(next, title);
    if (!validateExemplarHasNoPatientIdentity(next)) {
      notifications.show({
        title: 'Invalid exemplar',
        message: 'Exemplars must not reference a Patient or Encounter.',
        color: 'red',
      });
      return;
    }
    try {
      const saved = next.id
        ? await medplum.updateResource(next)
        : await medplum.createResource(next);
      setAvailableExemplars((prev) => {
        const others = prev.filter((qr) => qr.id !== saved.id);
        return [...others, saved];
      });
      setEditorOpen(false);
      notifications.show({
        title: 'Exemplar saved',
        message: 'Review the content carefully for residual identifying text.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Exemplar save failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const archiveExemplar = async (qr: QuestionnaireResponse): Promise<void> => {
    if (!qr.id) return;
    try {
      const latest = await medplum.readResource('QuestionnaireResponse', qr.id);
      latest.status = 'entered-in-error';
      await medplum.updateResource(latest);
      const ref = `QuestionnaireResponse/${qr.id}`;
      setAvailableExemplars((prev) => prev.filter((entry) => entry.id !== qr.id));
      setSelectedExemplarRefs((prev) => prev.filter((entry) => entry !== ref));
      notifications.show({
        title: 'Exemplar archived',
        message: 'It will no longer be offered for AI context selection.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Archive failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  const exemplarOptions = availableExemplars
    .filter((qr) => qr.status !== 'entered-in-error')
    .map((qr) => ({
      value: `QuestionnaireResponse/${qr.id}`,
      label: getExemplarTreatmentPlanTitle(qr) || qr.id || 'Untitled exemplar',
    }));

  return (
    <Paper p="xl" withBorder>
      <Stack gap="md">
        <div>
          <Title order={4} mb={4}>
            AI Treatment Planning
          </Title>
          <Text size="sm" c="dimmed">
            Configure per-template AI prompts and de-identified exemplar treatment plans.
            AI suggestions always require provider review and never finalize a plan.
          </Text>
        </div>

        <Select
          label="Treatment plan template"
          placeholder="Select a Questionnaire"
          data={templates.map((template) => ({
            value: template.id!,
            label: template.title || template.name || template.id!,
          }))}
          value={selectedTemplateId}
          onChange={setSelectedTemplateId}
          searchable
        />

        {!selectedTemplate && (
          <Alert color="yellow" variant="light">
            No specialized treatment-plan templates were found. Publish a treatment-planning
            Questionnaire first.
          </Alert>
        )}

        {selectedTemplate && (
          <>
            <Textarea
              label={
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={500}>Guided treatment conversation prompt</Text>
                  {!guidedHasOverride && (
                    <Badge size="xs" variant="light" color="gray">Using application default</Badge>
                  )}
                </Group>
              }
              description={GUIDED_PROMPT_HELP}
              value={guidedPrompt}
              onChange={(event) => setGuidedPrompt(event.currentTarget.value)}
              minRows={8}
              autosize
              styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
            />
            <Group justify="flex-start">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<IconRotate size={14} />}
                onClick={() => requestReset('guided')}
                disabled={guidedPrompt.trim() === DEFAULT_TREATMENT_PLAN_GUIDED_PROMPT.trim()}
              >
                Reset guided conversation prompt to default
              </Button>
            </Group>

            <Textarea
              label={
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={500}>Full treatment-plan generation prompt</Text>
                  {!generationHasOverride && (
                    <Badge size="xs" variant="light" color="gray">Using application default</Badge>
                  )}
                </Group>
              }
              description={GENERATION_PROMPT_HELP}
              value={generationPrompt}
              onChange={(event) => setGenerationPrompt(event.currentTarget.value)}
              minRows={8}
              autosize
              styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
            />
            <Group justify="flex-start">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<IconRotate size={14} />}
                onClick={() => requestReset('generation')}
                disabled={generationPrompt.trim() === DEFAULT_TREATMENT_PLAN_GENERATION_PROMPT.trim()}
              >
                Reset full generation prompt to default
              </Button>
            </Group>

            <MultiSelect
              label="Exemplars included in AI requests"
              description="Select de-identified exemplar treatment plans for this template. Real client plans cannot be used directly."
              data={exemplarOptions}
              value={selectedExemplarRefs}
              onChange={setSelectedExemplarRefs}
              searchable
            />

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Available exemplars
              </Text>
              {availableExemplars.filter((qr) => qr.status !== 'entered-in-error').length === 0 && (
                <Text size="sm" c="dimmed">
                  No exemplars yet. Create a synthetic exemplar to get started.
                </Text>
              )}
              {availableExemplars
                .filter((qr) => qr.status !== 'entered-in-error')
                .map((qr) => {
                  const ref = `QuestionnaireResponse/${qr.id}`;
                  const selected = selectedExemplarRefs.includes(ref);
                  return (
                    <Group key={qr.id} justify="space-between" wrap="nowrap">
                      <Checkbox
                        label={getExemplarTreatmentPlanTitle(qr) || qr.id}
                        checked={selected}
                        onChange={(event) => {
                          if (event.currentTarget.checked) {
                            setSelectedExemplarRefs((prev) => [...prev, ref]);
                          } else {
                            setSelectedExemplarRefs((prev) => prev.filter((entry) => entry !== ref));
                          }
                        }}
                      />
                      <Group gap="xs">
                        <Button size="compact-xs" variant="light" onClick={() => openEditExemplar(qr)}>
                          Edit
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="red"
                          leftSection={<IconArchive size={12} />}
                          onClick={() => void archiveExemplar(qr)}
                        >
                          Archive
                        </Button>
                      </Group>
                    </Group>
                  );
                })}
              <Button
                leftSection={<IconPlus size={14} />}
                variant="light"
                color="gold"
                onClick={openCreateExemplar}
                w="fit-content"
              >
                Create exemplar
              </Button>
            </Stack>

            <Group justify="flex-end">
              <Button variant="default" onClick={handleCancelEdits} disabled={!isDirty || saving}>
                Cancel
              </Button>
              <Button onClick={() => void handleSave()} loading={saving} disabled={!isDirty}>
                Save configuration
              </Button>
            </Group>
          </>
        )}
      </Stack>

      <Modal
        opened={resetConfirmKind !== null}
        onClose={() => setResetConfirmKind(null)}
        title="Reset to application default?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This replaces the {resetConfirmKind === 'guided' ? 'guided conversation' : 'full generation'}{' '}
            prompt shown above with the built-in default. Save the configuration afterward to apply it —
            your current text is discarded from this field only, not yet saved.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setResetConfirmKind(null)}>
              Cancel
            </Button>
            <Button color="gold" onClick={confirmReset}>
              Reset to default
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmCreateOpen}
        onClose={() => setConfirmCreateOpen(false)}
        title="Create de-identified exemplar"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Exemplars must not reference a real client. Copy only clinically relevant structure and
            review for residual identifying text before saving.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmCreateOpen(false)}>
              Cancel
            </Button>
            <Button color="gold" onClick={() => void createBlankExemplar()}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={editorOpen}
        onClose={() => setEditorOpen(false)}
        title="Edit exemplar treatment plan"
        size="xl"
      >
        <Stack gap="md">
          <Alert color="yellow" variant="light">
            This exemplar must stay de-identified. Do not enter client names, dates of birth, or other PHI.
          </Alert>
          <TextInput
            label="Exemplar title"
            required
            value={exemplarTitle}
            onChange={(event) => setExemplarTitle(event.currentTarget.value)}
          />
          {selectedTemplate && editingExemplar && (
            <TreatmentPlanDocumentFormSection
              questionnaire={selectedTemplate}
              savedResponse={editingExemplar}
              readOnlyLinkIds={new Set()}
              isEditable
              varCtx={EMPTY_VAR_CTX}
              onLiveResponseChange={setLiveExemplar}
            />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button color="gold" onClick={() => void saveExemplar()}>
              Save exemplar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
